// JitsiMeet.js
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

function JitsiMeet({ roomName, displayName, onClose, language = 'de', matchingId = null, peerUserId = null, peerRole = null }) {
  const jitsiContainerRef = useRef(null);
  const apiRef = useRef(null);

  const navigate = useNavigate();

  const startedAtRef = useRef(Date.now());
  const endedRef = useRef(false);
  const lastConnectionQualityRef = useRef(null);
  const remoteParticipantCountRef = useRef(0);
  const hadPartnerRef = useRef(false);

  const endAndGoToCallEnded = (reason = 'unknown') => {
    if (endedRef.current) return;
    endedRef.current = true;

    const durationSec = Math.max(
      0,
      Math.round((Date.now() - (startedAtRef.current || Date.now())) / 1000)
    );

    const connectionQuality = lastConnectionQualityRef.current;

    // Overlay schließen (Mentee-/Mentor-Dashboard)
    if (typeof onClose === 'function') {
      onClose();
    }

    const navState = {
      roomName,
      displayName,
      language,
      durationSec,
      connectionQuality,
      endedAt: new Date().toISOString(),
      reason,
      matchingId,
      peerUserId,
      peerRole,
      hadPartner: hadPartnerRef.current
    };

    // In die eigene Abschlussseite
    navigate('/call-ended', {
      replace: true,
      state: navState
    });
  };

  useEffect(() => {
    const domain = 'video.menvio.ai';

    // Reset pro Call
    startedAtRef.current = Date.now();
    endedRef.current = false;
    lastConnectionQualityRef.current = null;

    const options = {
      roomName,
      parentNode: jitsiContainerRef.current,
      userInfo: {
        displayName
      },
      configOverwrite: {
        disableDeepLinking: true,

        // alt / legacy – schadet nicht, wird aber meist ignoriert
        prejoinPageEnabled: false,

        // Prejoin-Seite sauber deaktivieren
        prejoinConfig: {
          enabled: false
        },

        // Sprache für diese Einbettung setzen
        defaultLanguage: language,

        // ✅ Custom CSS erzwingen (damit z.B. Watermark-Größe wirklich greift)
        // Wichtig: mit führendem Slash, damit es /css/custom.css auf video.menvio.ai ist.
        customCssUrl: '/css/custom.css',

        // ✅ Wir wollen *nicht* auf der Jitsi Close-Page “hängen bleiben”
        // (Falls die Option in deiner Version ignoriert wird, ist es egal – wir redirecten per Events.)
        enableClosePage: false,

        // ✅ App-Name für Strings wie “Danke für die Verwendung von {{appName}}”
        appName: 'MENVIO CALL-SYSTEM'
      },

      interfaceConfigOverwrite: {
        // automatische Spracherkennung deaktivieren
        LANG_DETECTION: false,
        // Standardsprache an Jitsi übergeben
        DEFAULT_LOCAL_LANGUAGE: language,

        // ✅ App-Name (UI/Close-Page/Strings)
        APP_NAME: 'MENVIO CALL-SYSTEM',
        NATIVE_APP_NAME: 'MENVIO CALL-SYSTEM',

        // explizit unser Logo setzen (mit Cache-Breaker)
        DEFAULT_LOGO_URL: 'images/watermark.svg?v=3',

        // Versuch, den offiziellen Link zu neutralisieren
        JITSI_WATERMARK_LINK: '',
        BRAND_WATERMARK_LINK: '',

        // ✅ Wichtig: Jitsi-Wasserzeichen aus, Brand-Wasserzeichen an
        SHOW_JITSI_WATERMARK: false,
        SHOW_BRAND_WATERMARK: true,

        SHOW_WATERMARK_FOR_GUESTS: false,
        SHOW_POWERED_BY: false,
        DISPLAY_WELCOME_PAGE_CONTENT: false,
        DISPLAY_WELCOME_FOOTER: false,
        DISPLAY_WELCOME_PAGE_ADDITIONAL_CARD: false,
        TOOLBAR_BUTTONS: [
          'microphone',
          'camera',
          'closedcaptions',
          'desktop',
          'fullscreen',
          'fodeviceselection',
          'hangup',
          'chat',
          'settings',
          'videoquality',
          'tileview',
          'select-background',
          'mute-everyone',
          'security'
        ]
      }
    };

    const api = new window.JitsiMeetExternalAPI(domain, options);
    apiRef.current = api;

    // ✅ Qualitätsindikator (wird im CallEndedPage als “Hinweis” nutzbar)
    // (Je nach Jitsi-Version kommt hier z.B. 0..100 oder ein kleinerer Range.)
    try {
      api.addEventListener('connectionQualityChanged', (e) => {
        const q = e?.connectionQuality;
        if (typeof q === 'number') {
          lastConnectionQualityRef.current = q;
        }
      });
    } catch {}

    // ✅ Call-Ende abfangen → sofort auf MENVIO Abschlussseite
    try {
      api.addEventListener('readyToClose', () => endAndGoToCallEnded('readyToClose'));
    } catch {}

    try {
      api.addEventListener('videoConferenceLeft', () => endAndGoToCallEnded('videoConferenceLeft'));
    } catch {}

    // Partner-Tracking: wurden andere Teilnehmer gesehen?
    try {
      api.addEventListener('participantJoined', () => {
        remoteParticipantCountRef.current += 1;
        hadPartnerRef.current = true;
      });
    } catch {}

    try {
      api.addEventListener('participantLeft', () => {
        remoteParticipantCountRef.current = Math.max(0, remoteParticipantCountRef.current - 1);
      });
    } catch {}

    return () => {
      try {
        api.dispose();
      } catch {}
      apiRef.current = null;
    };
  }, [roomName, displayName, language]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center">
      <div className="relative w-[90vw] h-[80vh] rounded-xl overflow-hidden">
        <button
          onClick={() => {
            // “X” = Call beenden + auf Abschlussseite
            try {
              apiRef.current?.executeCommand?.('hangup');
            } catch {}

            endAndGoToCallEnded('manualClose');
          }}
          className="absolute top-2 right-2 z-30 bg-white text-black px-3 py-1 rounded shadow"
        >
          ✕
        </button>

        {/* Jitsi-Container / IFrame */}
        <div
          ref={jitsiContainerRef}
          className="w-full h-full relative z-10"
        ></div>

        {/* Click-Blocker über dem Logo-Bereich:
            - liegt VOR dem IFrame (z-20 > z-10)
            - fängt alle Klicks ab, damit kein Link mehr ausgelöst wird
         */}
        <div
          className="absolute z-20"
          style={{
            top: 16,
            left: 16,
            width: 220,
            height: 120,
            cursor: 'default',
            background: 'transparent'
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      </div>
    </div>
  );
}

export default JitsiMeet;
